import { useEffect, useState } from "react";
import { startRegistration, type PublicKeyCredentialCreationOptionsJSON } from "@simplewebauthn/browser";
import { VAT_MODES } from "@apphq/shared/metrics";
import type { SettingsResponse } from "@apphq/shared";
import { apiGet, apiPost, errorMessage } from "../api";
import { Segmented } from "../components/Controls";
import { PageHeader, useShell } from "../components/Shell";
import { AppIcon, Card, Chip, CommandCenterButton, Dot, EmptyState, ErrorState, LoadingState } from "../components/ui";
import { adminSend, getAppDetail, mintKey, patchApp, syncAsc } from "../lib/api-admin";
import { VAT_LABELS, dateNl, dateTimeNl, relativeNl, safeHttpUrl } from "../lib/format";
import { usePrefs } from "../lib/prefs";
import type { Theme } from "../lib/prefs-logic";
import { useLocation } from "../lib/router";
import { useAsync } from "../lib/useAsync";
import { disablePush, enablePush, pushState, sendTestPush, type PushState } from "../push";

export function Settings() {
  const { settingsApps: apps } = useShell();
  const { search, setSearch } = useLocation();
  const appId = search.get("app");
  const scope = appId ? "app" : "account";

  return (
    <>
      <PageHeader title="Instellingen" subtitle={scope === "account" ? "Account en weergave" : "Instellingen per app"}>
        <div className="segmented" role="group" aria-label="Onderdeel">
          <button className={scope === "account" ? "seg active" : "seg"} onClick={() => setSearch({ app: null })}>Account</button>
          {apps.length > 0 && (
            <select className={scope === "app" ? "seg active seg-select" : "seg seg-select"} value={appId ?? ""} onChange={(e) => setSearch({ app: e.target.value || null })} aria-label="Per app">
              <option value="">Per app…</option>
              {apps.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.display_name ?? a.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </PageHeader>
      {scope === "account" ? <AccountSettings /> : <AppSettings id={appId as string} />}
    </>
  );
}

interface Passkey {
  credential_id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
}

function AccountSettings() {
  const { settings } = useShell();
  const { vat, theme, set } = usePrefs();
  const passkeys = useAsync(async () => {
    const r = await apiGet("/api/auth/passkeys");
    if (!r.ok) throw new Error(await errorMessage(r, "Passkeys laden mislukt"));
    return ((await r.json()) as { passkeys: Passkey[] }).passkeys;
  }, []);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function addPasskey() {
    setBusy(true);
    setMsg(null);
    try {
      const o = await apiPost("/api/auth/passkeys/add/options");
      if (!o.ok) throw new Error(await errorMessage(o, "Starten mislukt"));
      const options = ((await o.json()) as { options: PublicKeyCredentialCreationOptionsJSON }).options;
      const response = await startRegistration({ optionsJSON: options });
      const v = await apiPost("/api/auth/passkeys/add/verify", { response, name: deviceName() });
      if (!v.ok) throw new Error(await errorMessage(v, "Toevoegen mislukt"));
      setMsg("Passkey toegevoegd.");
      passkeys.reload();
    } catch (e) {
      setMsg(e instanceof Error && e.name === "NotAllowedError" ? "Geannuleerd." : e instanceof Error ? e.message : "Toevoegen mislukt");
    } finally {
      setBusy(false);
    }
  }

  async function removePasskey(p: Passkey) {
    if (!window.confirm(`Passkey "${p.name}" verwijderen?`)) return;
    setBusy(true);
    setMsg(null);
    try {
      await adminSend("DELETE", `/api/auth/passkeys/${encodeURIComponent(p.credential_id)}`);
      passkeys.reload();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Verwijderen mislukt");
    } finally {
      setBusy(false);
    }
  }

  async function logoutAll() {
    if (!window.confirm("Overal afmelden? Je moet daarna opnieuw inloggen met je passkey.")) return;
    await apiPost("/api/auth/logout-all");
    window.location.href = "/";
  }

  async function logout() {
    await apiPost("/api/auth/logout");
    window.location.href = "/";
  }

  return (
    <>
      <Card title="Inloggen" aside={<Chip>Alleen jij</Chip>}>
        {passkeys.status === "error" && <ErrorState message={passkeys.error} onRetry={passkeys.reload} />}
        {!passkeys.data && passkeys.status === "loading" && <LoadingState />}
        {passkeys.data && passkeys.data.length === 0 && <EmptyState title="Geen passkeys gevonden" />}
        {passkeys.data && (
          <ul className="setting-list">
            {passkeys.data.map((p, _i, all) => (
              <li key={p.credential_id}>
                <div>
                  <strong>{p.name}</strong>
                  <div className="muted">Aangemaakt {dateNl(p.created_at)} · {p.last_used_at ? `laatst gebruikt ${relativeNl(p.last_used_at)}` : "nog niet gebruikt"}</div>
                </div>
                <button className="pill pill-danger" disabled={busy || all.length <= 1} title={all.length <= 1 ? "De laatste passkey kan niet worden verwijderd" : undefined} onClick={() => removePasskey(p)}>
                  Verwijderen
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="button-row">
          <button className="pill pill-primary" onClick={addPasskey} disabled={busy}>Passkey toevoegen</button>
          <button className="pill" onClick={logoutAll}>Overal afmelden</button>
          <button className="pill" onClick={logout}>Uitloggen</button>
        </div>
        {msg && <p className="note">{msg}</p>}
      </Card>

      <Card title="Koppelingen">
        {!settings ? (
          <LoadingState />
        ) : (
          <ul className="setting-list">
            <StatusRow label="In-App Purchase-sleutel" ok={settings.apple_keys} okText="Ingesteld" badText="Ontbreekt" />
            <StatusRow label="App Store Connect API-sleutel" ok={settings.asc_keys} okText={`Ingesteld · laatste sync ${relativeNl(settings.last_asc_sync)}`} badText="Ontbreekt" />
            <StatusRow label="Web-push (VAPID)" ok={settings.vapid} okText="Ingesteld" badText="Ontbreekt" />
            <AnalyticsRow analytics={settings.analytics} ascKeys={settings.asc_keys} />
          </ul>
        )}
      </Card>

      <PushCard />

      <Card title="Weergave">
        <ul className="setting-list">
          <li>
            <div>
              <strong>Valuta</strong>
              <div className="muted">Rapportage is altijd in euro</div>
            </div>
            <Chip>EUR</Chip>
          </li>
          <li>
            <div>
              <strong>Standaard weergave</strong>
              <div className="muted">Geldt op dit apparaat; schakel ook bovenaan in elk scherm</div>
            </div>
            <Segmented label="Standaard weergave" value={vat} options={VAT_MODES.map((m) => ({ value: m, label: VAT_LABELS[m] }))} onChange={(v) => set({ vat: v })} />
          </li>
          <li>
            <div>
              <strong>Thema</strong>
              <div className="muted">Automatisch volgt je systeeminstelling</div>
            </div>
            <Segmented<Theme> label="Thema" value={theme} options={[{ value: "auto", label: "Auto" }, { value: "light", label: "Licht" }, { value: "dark", label: "Donker" }]} onChange={(v) => set({ theme: v })} />
          </li>
        </ul>
      </Card>

      <Card title="Automatisering">
        <ul className="setting-list">
          <li>
            <div>
              <strong>Nieuwe apps automatisch koppelen</strong>
              <div className="muted">Altijd aan zodra de App Store Connect-sleutel is ingesteld</div>
            </div>
            <Chip tone={settings?.asc_keys ? "good" : "neutral"}>{settings?.asc_keys ? "Aan" : "Uit"}</Chip>
          </li>
          <li>
            <div>
              <strong>Bestaande notificatie-URL's overschrijven</strong>
              <div className="muted">Per geval via de Inbox ("Overnemen" of "Laten staan")</div>
            </div>
            <Chip>Per melding</Chip>
          </li>
          <li>
            <div>
              <strong>Dagelijkse testnotificatie</strong>
              <div className="muted">Nog niet beschikbaar</div>
            </div>
            <Chip>Nog niet actief</Chip>
          </li>
        </ul>
      </Card>
    </>
  );
}

function deviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Macintosh/.test(ua)) return "Mac";
  return "Passkey";
}

function AnalyticsRow({ analytics: a, ascKeys }: { analytics: SettingsResponse["analytics"]; ascKeys: boolean }) {
  const m = !ascKeys
    ? { tone: "neutral" as const, chip: "Nog niet actief", sub: "Zodra de App Store Connect-sleutel is ingesteld" }
    : a.active && a.last_report_date
      ? { tone: "good" as const, chip: "Actief", sub: `Laatste rapport ${dateNl(a.last_report_date)}` }
      : a.active
        ? { tone: "warn" as const, chip: "Aangevraagd", sub: "Eerste data verschijnt ~24–48u na de aanvraag" }
        : { tone: "warn" as const, chip: "Aangevraagd", sub: "Rapporten worden aangevraagd" };
  return (
    <li>
      <div>
        <strong>Analytics-rapporten</strong>
        <div className="muted">{m.sub}</div>
      </div>
      <Chip tone={m.tone}>{m.chip}</Chip>
    </li>
  );
}

function StatusRow({ label, ok, okText, badText }: { label: string; ok: boolean; okText: string; badText: string }) {
  return (
    <li>
      <div>
        <strong>{label}</strong>
        <div className="muted">{ok ? okText : badText}</div>
      </div>
      <Dot tone={ok ? "good" : "bad"} title={ok ? "OK" : "Ontbreekt"} />
    </li>
  );
}

function PushCard() {
  const [state, setState] = useState<PushState | "loading">("loading");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    pushState().then(setState).catch(() => setState("unsupported"));
  }, []);
  const standalone = typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)").matches;

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Mislukt");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Notificaties">
      <p className="muted tight">Web-push naar dit apparaat (nieuwe aankoop, refund, dagrapport).</p>
      {state === "loading" && <LoadingState />}
      {state === "unsupported" && <p className="muted">Niet ondersteund in deze browser. Op iPhone: zet AppHQ eerst op je beginscherm en open de app van daaruit.</p>}
      {state === "off" && (
        <>
          <button className="pill pill-primary" disabled={busy} onClick={() => run(async () => {
            const s = await enablePush();
            setState(s);
            setMsg(s === "on" ? "Notificaties staan aan op dit apparaat." : s === "off" ? "Toestemming geweigerd." : null);
          })}>
            Notificaties aanzetten
          </button>
          {!standalone && <p className="muted tight">Tip: op iPhone werkt dit alleen vanuit de app op je beginscherm.</p>}
        </>
      )}
      {state === "on" && (
        <div className="button-row">
          <button className="pill pill-primary" disabled={busy} onClick={() => run(async () => {
            const r = await sendTestPush();
            setMsg(`Verzonden naar ${r.sent} apparaat/apparaten${r.pruned ? `, ${r.pruned} opgeruimd` : ""}.`);
          })}>
            Testnotificatie
          </button>
          <button className="pill" disabled={busy} onClick={() => run(async () => {
            await disablePush();
            setState("off");
            setMsg("Uitgezet op dit apparaat.");
          })}>
            Uitzetten
          </button>
        </div>
      )}
      {msg && <p className="note">{msg}</p>}
    </Card>
  );
}

function AppSettings({ id }: { id: string }) {
  const { refresh } = useShell();
  const detail = useAsync(() => getAppDetail(id), [id]);
  const [url, setUrl] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);

  const app = detail.data?.app;
  useEffect(() => {
    if (app) setUrl(app.command_center_url ?? "");
  }, [app?.command_center_url, app?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (app) setNameDraft(app.display_name ?? "");
  }, [app?.display_name, app?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (detail.status === "error") return <ErrorState message={detail.error} onRetry={detail.reload} />;
  if (!app || url === null || nameDraft === null) return <LoadingState />;

  const shownName = app.display_name ?? app.name;
  const nameTrimmed = nameDraft.trim();
  const nameDirty = nameTrimmed !== (app.display_name ?? "");

  const trimmed = url.trim();
  const valid = trimmed === "" || safeHttpUrl(trimmed) !== null;
  const dirty = trimmed !== (app.command_center_url ?? "");
  const smallBiz = app.apple_commission <= 0.15;

  async function run(fn: () => Promise<string | void>) {
    setBusy(true);
    setMsg(null);
    try {
      const text = await fn();
      if (text) setMsg({ tone: "ok", text });
      detail.reload();
      refresh();
    } catch (e) {
      setMsg({ tone: "err", text: e instanceof Error ? e.message : "Mislukt" });
    } finally {
      setBusy(false);
    }
  }

  const activeKeys = detail.data?.keys.filter((k) => k.kind === "public" && !k.revoked_at) ?? [];

  return (
    <>
      {msg && <div className={msg.tone === "ok" ? "banner banner-ok" : "banner banner-warn"} role="status">{msg.text}</div>}

      <Card>
        <div className="customer-head">
          <AppIcon name={shownName} url={app.icon_url} size={52} seed={app.id} />
          <div className="customer-title">
            <strong className="customer-id">{shownName}</strong>
            <div className="muted mono">{app.bundle_id ?? "geen bundle-ID"}</div>
            <div className="muted">Apple ID: {app.apple_app_id ?? app.asc_app_id ?? "—"}</div>
          </div>
        </div>
      </Card>

      <Card title="Weergavenaam">
        <p className="muted tight">Naam zoals die in het dashboard en in meldingen staat. Leeg laten betekent: de naam uit App Store Connect ({app.name}).</p>
        <div className="field-row">
          <input className="search search-block" type="text" maxLength={200} placeholder={app.name} value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} aria-label="Weergavenaam" />
          <button className="pill pill-primary" disabled={busy || !nameDirty} onClick={() => run(async () => {
            await patchApp(id, { display_name: nameTrimmed === "" ? null : nameTrimmed });
            return "Opgeslagen.";
          })}>
            Opslaan
          </button>
        </div>
      </Card>

      <Card title="Command center">
        <p className="muted tight">Alleen een doorlink. Leeg laten betekent: geen knop.</p>
        <div className="field-row">
          <input className="search search-block" type="url" inputMode="url" placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} aria-label="Command-center-URL" aria-invalid={!valid} />
          <button className="pill pill-primary" disabled={busy || !dirty || !valid} onClick={() => run(async () => {
            await patchApp(id, { command_center_url: trimmed === "" ? null : trimmed });
            return "Opgeslagen.";
          })}>
            Opslaan
          </button>
        </div>
        {!valid && <p className="error-text">Gebruik een volledige http(s)-link.</p>}
        <div className="preview-row">
          <span className="muted">Voorbeeld:</span>
          <CommandCenterButton url={trimmed} showUnset />
        </div>
      </Card>

      <Card title="Notificatie-URL's (Apple V2)">
        <ul className="setting-list">
          <li>
            <div>
              <strong>Status</strong>
              <div className="muted">Productie en sandbox worden automatisch gezet via App Store Connect. Laatste sync: {app.asc_synced_at ? dateTimeNl(app.asc_synced_at) : "nog niet"}</div>
            </div>
            <Chip tone={app.notif_url_state === "ok" ? "good" : app.notif_url_state === "conflict" ? "bad" : "neutral"}>
              {app.notif_url_state === "ok" ? "Automatisch gezet · V2" : app.notif_url_state === "conflict" ? "Conflict (zie Inbox)" : app.notif_url_state ?? "Onbekend"}
            </Chip>
          </li>
        </ul>
        <div className="button-row">
          <button className="pill" disabled={busy || !app.bundle_id} onClick={() => run(async () => {
            const r = await syncAsc(app.bundle_id ?? undefined);
            return typeof r.skipped === "string" ? `Overgeslagen: ${r.skipped}` : "Sync afgerond.";
          })}>
            Nu testen
          </button>
        </div>
      </Card>

      <Card title="Facturatie">
        <ul className="setting-list">
          <li>
            <div>
              <strong>Small Business Program</strong>
              <div className="muted">Commissie {Math.round(app.apple_commission * 100)}%. Geldt alleen voor nieuwe transacties.</div>
            </div>
            <button className="pill" disabled={busy} onClick={() => run(async () => {
              await patchApp(id, { apple_commission: smallBiz ? 0.3 : 0.15 });
              return smallBiz ? "Commissie op 30% gezet." : "Commissie op 15% gezet.";
            })}>
              {smallBiz ? "Uitzetten" : "Aanzetten"}
            </button>
          </li>
        </ul>
      </Card>

      <Card title="Publieke SDK-sleutel">
        <p className="muted tight">De sleutel wordt maar één keer getoond. Een actieve sleutel: {activeKeys.length > 0 ? `${activeKeys[0]?.prefix}…` : "geen"}.</p>
        {newKey && (
          <div className="key-box">
            <code>{newKey}</code>
            <button className="pill" onClick={() => navigator.clipboard?.writeText(newKey).then(() => setMsg({ tone: "ok", text: "Sleutel gekopieerd." }), () => undefined)}>
              Kopiëren
            </button>
          </div>
        )}
        <div className="button-row">
          <button className="pill" disabled={busy} onClick={() => {
            if (!window.confirm("Nieuwe sleutel maken en de huidige intrekken? Apps met de oude sleutel stoppen met werken tot ze zijn bijgewerkt.")) return;
            void run(async () => {
              const k = await mintKey(id, true);
              setNewKey(k.public_key);
              return "Nieuwe sleutel gemaakt; de vorige is ingetrokken.";
            });
          }}>
            Vernieuwen
          </button>
        </div>
      </Card>

      <Card title="Verbergen uit dashboard">
        <p className="muted tight">Een verborgen app staat niet in de zijbalk, het overzicht, gebruik en klanten, en telt niet mee in de totalen. Data blijft binnenkomen.</p>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={app.hidden === 1}
            disabled={busy}
            onChange={(e) => {
              const hidden = e.target.checked;
              void run(async () => {
                await patchApp(id, { hidden });
                return hidden ? "App verborgen uit het dashboard." : "App is weer zichtbaar.";
              });
            }}
          />{" "}
          Verbergen uit dashboard
        </label>
      </Card>

      <Card title="Nieuw-account-notificatie">
        <p className="muted tight">Stuur een push zodra deze app meldt dat er een nieuw account is aangemaakt. Uit = je krijgt hier niets van.</p>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={app.notify_new_account !== 0}
            disabled={busy}
            onChange={(e) => {
              const notify_new_account = e.target.checked;
              void run(async () => {
                await patchApp(id, { notify_new_account });
                return notify_new_account ? "Push bij nieuw account staat aan." : "Push bij nieuw account staat uit.";
              });
            }}
          />{" "}
          Push bij een nieuw account in deze app
        </label>
      </Card>

      <Card title="Archiveren">
        <p className="muted tight">Gearchiveerde apps verdwijnen uit de zijbalk; data blijft bewaard.</p>
        <button className="pill pill-danger" disabled={busy} onClick={() => {
          const archived = app.status === "archived";
          if (!archived && !window.confirm(`${shownName} archiveren?`)) return;
          void run(async () => {
            await patchApp(id, { status: archived ? "active" : "archived" });
            return archived ? "App is weer actief." : "App gearchiveerd.";
          });
        }}>
          {app.status === "archived" ? "Terugzetten" : "Archiveren"}
        </button>
      </Card>
    </>
  );
}
